import { zodResolver } from "@hookform/resolvers/zod";
import { format, isSameDay, isValid, parse, parseISO } from "date-fns";
import { LucideIcon } from "lucide-react";
import {
  DefaultValues,
  FieldValues,
  Resolver,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { z } from "zod";

export interface FormFieldClasses {
  container?: string;
  label?: string;
  input?: string;
  textarea?: string;
  select?: any;
  checkbox?: string;
  slider?: string;
  radio?: string;
  file?: string;
  error?: string;
  helperText?: string;
  required?: string;
  placeholder?: string;
  MultiSelectDropdown?: string;
  div?: string;
  verifyButton?: string;
  helperIcon?: string;
  tooltip?: string;
  radioCard?: string;
  radioCardChecked?: string;
  description?: string;
  radioGroup?: string;
}

export interface FormClasses {
  form?: string;
  fieldContainer?: string;
  div?: string;
  buttonContainer?: string;
  fieldGroup?: string;
  label?: string;
  input?: string;
  textarea?: string;
  select?: string;
  checkbox?: string;
  slider?: string;
  container?: string;
  inputContainer?: string;
  iconContainer?: string;
  radioItem?: string;
  radioGroup?: string;
  radioContainer?: string;
  radio?: string;
  file?: string;
  button?: string;
  submitButton?: string;
  resetButton?: string;
  error?: string;
  helperText?: string;
  required?: string;
  disabled?: string;
  focused?: string;
  valid?: string;
  invalid?: string;
  loading?: string;
  verifyButton?: string;
  helperIcon?: string;
  tooltip?: string;
}

export type FormFieldType =
  | "text"
  | "select"
  | "number"
  | "date"
  | "time"
  | "array";

export interface FormField {
  name: string;
  label?: string;

  disabledDate?: (current: Date) => boolean;
  fields?: FormField[];
  labelStyle?: string;
  groupHelperText?: string;
  groupColorNeeded?: boolean;
  placeholderNeeded?: boolean;

  type:
    | "text"
    | "autoPopulated"
    | "email"
    | "password"
    | "number"
    | "decimal"
    | "negative-decimal"
    | "datepicker"
    | "time"
    | "select"
    | "multi-select"
    | "textarea"
    | "checkbox"
    | "radio"
    | "radio-card"
    | "file"
    | "multi-file"
    | "phone"
    | "url"
    | "hidden"
    | "range"
    | "switch"
    | "color"
    | "form-array"
    | "label"
    | "rich-text"
    | "html-text"
    | "checkbox-group"
    | "slider"
    | "tags"
    | "text-with-placeholder"
    | "textarea-with-placeholder"
    | "country-phone"
    | "custom"
    | "button";
  required?: boolean;
  placeholder?: string;
  customRender?: (
    field: FormField,
    form: any,
    error?: string,
  ) => React.ReactNode;
  options?: Array<{
    label: string;
    value: number | string | boolean;
    disabled?: boolean;
    description?: string;
  }>;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  rows?: number;
  cols?: number;
  size?: "small" | "medium" | "large";
  variant?: "outlined" | "filled" | "standard";
  customValidation?: (value: any) => string | boolean;
  dependsOn?: string;
  showWhen?: (formValues: any, fieldPath?: any) => boolean;
  onChange?: (value: any) => void;
  onBlur?: (value: any) => void;
  onFocus?: (value: any) => void;
  defaultValue?: any;
  disabled?: boolean | ((formValues: any) => boolean);
  readonly?: boolean | ((formValues: any) => boolean);
  helperText?: string;
  showVerifyButton?: boolean;
  verifyButtonText?: string;
  onVerify?: (value: any, field: FormField) => Promise<boolean> | boolean;
  verificationStatus?: "unverified" | "verifying" | "verified" | "failed";
  verifiedText?: string;
  verifyButtonDisabled?: boolean;
  showHelperTooltip?: boolean;
  tooltipText?: any;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  classes?: FormFieldClasses;
  className?: string;
  validation?: any;
  containerClassName?: string;
  radioGroupClassName?: string;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  width?: string | number;
  fullWidth?: boolean;
  inline?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  group?: string;
  groupOrder?: number;
  groupColor?: string;
  groupBgColor?: string;
  groupBorderColor?: string;
  groupIcon?: React.ReactNode;
  groupBorderNeeded?: boolean;
  closeOnSelect?: boolean;
  maxDisplayItems?: number;
  searchable?: boolean;
  clearable?: boolean;
  // itemFields?: FormField[];
  // UPDATE THIS LINE - Support both array and function
  itemFields?: FormField[] | ((index: number, formValues?: any) => FormField[]);

  minItems?: number;
  maxItems?: number;
  addButtonText?: string;
  removeButtonText?: string;
  emptyMessage?: string;
  showAddButton?: boolean;
  showEmptyAddButton?: boolean;
  showRemoveButton?: boolean;
  showReorderButtons?: boolean;
  onSearchChange?: (term) => void;
  maxSelection?: number;
  value?: any;
  // Date validation properties
  allowFuture?: boolean; // Allow future dates (default: true)
  allowPast?: boolean; // Allow past dates (default: true)
  allowNegativeValue?: boolean; // If false, negative values are blocked
  placeholders?: Array<{
    group: string;
    items: Array<{
      label: string;
      value: string;
    }>;
  }>;
  buttonLabel?: string;
  buttonIcon?: React.ReactNode;
  buttonVariant?:
    | "default"
    | "outline"
    | "ghost"
    | "destructive"
    | "secondary"
    | "link";
  onClick?: () => void;
  buttonDisabled?: boolean;
  buttonFullWidth?: boolean;
  isSearchable?: boolean;
  /** Dynamic options resolver — called per row in SharedFormArray with the row's current data.
   *  When provided, takes priority over static `options`. */
  getOptions?: (
    rowData: any,
  ) => Array<{
    label: string;
    value: string | number | boolean;
    disabled?: boolean;
    description?: string;
  }>;
}

export interface FormConfig {
  fields: FormField[];
  onSubmit?: (data: any) => void | Promise<void>;
  mode?: "onChange" | "onBlur" | "onSubmit";
  shouldUnregister?: boolean;
  classes?: FormClasses;
  className?: string;
  style?: React.CSSProperties;
  layout?: "vertical" | "horizontal" | "inline" | "grid";
  columns?: number;
  spacing?: "none" | "small" | "medium" | "large" | "comfortable";
  resetOnSubmit?: boolean;
  showResetButton?: boolean;
  showSubmitButton?: boolean;
  submitButtonText?: any;
  resetButtonText?: string;
  showProgress?: boolean;
  validateOnMount?: boolean;
  defaultValues?: any;
  icon?: LucideIcon;
  resetIcon?: LucideIcon;
}

export const defaultFormClasses: FormClasses = {
  form: "dynamic-form",
  fieldContainer: "form-field-container",
  fieldGroup: "form-field-group",
  label: "form-label",
  slider: "form-slider",
  input: "form-input",
  textarea: "form-textarea",
  select: "form-select",
  checkbox: "form-checkbox",
  radio: "form-radio",
  file: "form-file",
  button: "form-button",
  submitButton: "form-submit-button",
  resetButton: "form-reset-button",
  error: "form-error",
  helperText: "form-helper-text",
  required: "form-required",
  disabled: "form-disabled",
  focused: "form-focused",
  valid: "form-valid",
  invalid: "form-invalid",
  loading: "form-loading",
  verifyButton: "form-verify-button",
  helperIcon: "form-helper-icon",
  tooltip: "form-tooltip",
};

class EnhancedFormService {
  private static DATE_FORMATS = {
    "DD/MM/YYYY": "dd/MM/yyyy",
    "MM/DD/YYYY": "MM/dd/yyyy",
    "YYYY-MM-DD": "yyyy-MM-dd",
    "DD-MM-YYYY": "dd-MM-yyyy",
  };

  // Schema cache to prevent infinite recursion
  private static schemaCache = new Map<string, z.ZodTypeAny>();

  static formatFieldName(name: string): string {
    return name
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  static createForm<T extends FieldValues = FieldValues>(
    config: FormConfig,
  ): UseFormReturn<T> {
    const defaultValues = this.getDefaultValues(config.fields);

    // Dynamic resolver — rebuilds schema with current values on every validation
    // so showWhen-hidden fields are skipped at validation time.
    const resolver: Resolver<T> = async (values, context, options) => {
      const schema = EnhancedFormService.createValidationSchema(
        config.fields,
        values,
      );
      const zodRes = zodResolver(schema) as unknown as Resolver<T>;
      return zodRes(values, context, options);
    };

    const form = useForm<T>({
      resolver,
      defaultValues: defaultValues as DefaultValues<T>,
      mode: config.mode || "onBlur",
      shouldUnregister: config.shouldUnregister ?? false,
    });

    if (config.validateOnMount) {
      requestAnimationFrame(() => form.trigger());
    }

    return form;
  }

  static async handleFieldVerification(
    field: FormField,
    value: any,
    onStatusChange?: (
      fieldName: string,
      status: FormField["verificationStatus"],
    ) => void,
  ): Promise<boolean> {
    if (!field.onVerify || !field.showVerifyButton) {
      return false;
    }

    try {
      onStatusChange?.(field.name, "verifying");
      const result = await field.onVerify(value, field);
      onStatusChange?.(field.name, result ? "verified" : "failed");
      return result;
    } catch (error) {
      onStatusChange?.(field.name, "failed");
      return false;
    }
  }

  static getVerifyButtonText(field: FormField): string {
    const status = field.verificationStatus || "unverified";
    switch (status) {
      case "verifying":
        return "Verifying...";
      case "verified":
        return field.verifiedText || "Verified";
      case "failed":
        return field.verifyButtonText || "Retry";
      default:
        return field.verifyButtonText || "Verify";
    }
  }

  static isVerifyButtonDisabled(field: FormField, value: any): boolean {
    if (field.verifyButtonDisabled) return true;
    if (field.verificationStatus === "verifying") return true;
    if (!value || value.toString().trim() === "") return true;

    if (field.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(value);
    }

    if (field.type === "phone") {
      // If field is not required, skip regex validation when value is empty
      if (!field.required && !value) return false;

      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      return !phoneRegex.test(value);
    }
    return false;
  }

  public static createValidationSchema(
    fields: FormField[],
    formValues?: any, // ← NEW
    visited = new Set<string>(),
  ): z.ZodTypeAny {
    // Skip cache when formValues provided — validation now depends on runtime values
    const shouldCache = !formValues;
    const cacheKey = fields
      .map(
        (f) =>
          `${f.name}:${f.label || f.name}:${f.type}:${
            f.required
          }:${JSON.stringify(f.options || [])}`,
      )
      .sort()
      .join("|");

    if (shouldCache && this.schemaCache.has(cacheKey)) {
      return this.schemaCache.get(cacheKey)!;
    }

    const schemaShape: Record<string, any> = {};

    fields.forEach((field) => {
      if (visited.has(field.name)) {
        console.warn(`Circular dependency detected for field: ${field.name}`);
        schemaShape[field.name] = z.any().optional();
        return;
      }

      if (field.type === "hidden") {
        schemaShape[field.name] = z.any().optional();
        return;
      }

      // ── NEW: Skip hidden fields entirely ──
      if (field.showWhen && formValues) {
        try {
          if (!field.showWhen(formValues)) {
            schemaShape[field.name] = z.any().optional();
            return;
          }
        } catch {
          // fail-open: validate normally if showWhen throws
        }
      }

      let fieldSchema: any;

      switch (field.type) {
        case "datepicker":
          fieldSchema = z
            .string()
            .optional()
            .refine((value) => {
              if (!value) return true; // allow empty
              const parsed = this.parseDate(value);
              return parsed !== null;
            }, "Please enter a valid date");
          break;

        case "slider":
          fieldSchema = z
            .number()
            .min(field.min || 0, `Minimum value is ${field.min || 0}`)
            .max(field.max || 100, `Maximum value is ${field.max || 100}`);
          break;

        case "email":
          if (field.required) {
            fieldSchema = z
              .string()
              .min(1, `${field.label || field.name} is required`)
              .email("Please enter a valid email address");
          } else {
            fieldSchema = z.string().optional();
          }
          break;

        case "password": {
          if (!field.required) {
            fieldSchema = z.string().optional();
            break;
          }

          fieldSchema = z
            .string()
            .min(
              field.minLength || 6,
              `Password must be at least ${field.minLength || 6} characters`,
            )
            .regex(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              "Password must contain uppercase, lowercase, and number",
            );

          break;
        }

        case "number":
          if (field.required) {
            fieldSchema = z.preprocess(
              (val) => {
                if (val === "" || val === undefined || val === null)
                  return undefined;
                return Number(val);
              },
              z.number({
                required_error: `${field.label || field.name} is required`,
                invalid_type_error: `${field.label || field.name} must be a valid number`,
              }),
            );
            if (field.min !== undefined)
              fieldSchema = (fieldSchema as z.ZodTypeAny).pipe(
                z.number().min(field.min, `Minimum value is ${field.min}`),
              );
            if (field.max !== undefined)
              fieldSchema = (fieldSchema as z.ZodTypeAny).pipe(
                z.number().max(field.max, `Maximum value is ${field.max}`),
              );
          } else {
            fieldSchema = z.preprocess(
              (val) => {
                if (val === "" || val === undefined || val === null)
                  return undefined;
                return Number(val);
              },
              z
                .number({
                  invalid_type_error: `${field.label || field.name} must be a valid number`,
                })
                .optional(),
            );
          }
          break;

        case "decimal":
          if (field.required) {
            fieldSchema = z
              .union([
                z
                  .number()
                  .refine((val) => /^\d+(\.\d{0,7})?$/.test(val.toString()), {
                    message: "Please enter a valid decimal number",
                  }),
                z
                  .string()
                  .regex(
                    /^\d+(\.\d{0,7})?$/,
                    "Please enter a valid decimal number",
                  ),
              ])
              .transform((val) =>
                typeof val === "string" ? parseFloat(val) : val,
              );
          } else {
            fieldSchema = z.union([z.string(), z.number()]).optional();
          }
          break;

        // case "negative-decimal":
        //   fieldSchema = z
        //     .union([z.string(), z.number()])
        //     .transform((val) =>
        //       val === undefined || val === null ? "" : String(val)
        //     )
        //     .refine((val) => /^[-]?\d+(\.\d{0,7})?$/.test(val), {
        //       message: "Please enter a valid decimal number",
        //     })
        //     .optional();
        //   break;

        case "negative-decimal":
          fieldSchema = z
            .union([z.string(), z.number()])
            .transform((val) =>
              val === undefined || val === null ? "" : String(val),
            )
            .refine((val) => val === "" || /^-?\d+(\.\d{0,7})?$/.test(val), {
              message: "Please enter a valid decimal number",
            })
            .optional();
          break;

        case "phone":
          fieldSchema = z.string().superRefine((val, ctx) => {
            // Allow empty when not required
            if (!field.required && val.trim() === "") return;

            // Ensure there are at least 6 digits
            const digits = val.replace(/[^\d]/g, "");
            if (digits.length < 6) {
              // Extract the selected country code (e.g. "+380" from "+380 (22) 222 22 22")
              // react-phone-input-2 always adds a plus followed by up to 4 digits at the start.
              const match = val.match(/^(\+\d{1,4})/);
              const dialCode = match ? match[1] : "+1";

              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Please enter a valid phone number (e.g., ${dialCode} XXXXXXXXXX)`,
              });
            }
          });
          break;

        case "url":
          fieldSchema = z.string().refine((val) => {
            if (!val || val.trim() === "") return true;
            try {
              new URL(val);
              return true;
            } catch {
              return false;
            }
          }, "Please enter a valid URL");
          break;

        case "time":
          if (field.required) {
            fieldSchema = z.string().refine((val) => {
              if (!val || val.trim() === "") return true;
              try {
                new Date(val);
                return true;
              } catch {
                return false;
              }
            }, "Please enter a valid time");
          } else {
            fieldSchema = z.string().optional();
          }
          break;

        case "select": {
          const selectBase = z
            .union([
              z.string(),
              z.number(),
              z.array(z.any()),
              z.null(),
              z.undefined(),
            ])
            .transform((val) => {
              if (val === null || val === undefined) return "";
              if (Array.isArray(val)) {
                return val.length > 0 ? String(val[0]) : "";
              }
              return String(val);
            });

          if (field.required) {
            fieldSchema = selectBase.refine(
              (val) => {
                if (!val || val === "null" || val === "undefined") return false;
                if (field.options && field.options.length > 0) {
                  return field.options.some(
                    (opt) => String(opt.value) === val && !opt.disabled,
                  );
                }
                return true;
              },
              { message: `${field.label || field.name} is required` },
            );
          } else {
            fieldSchema = selectBase.optional().refine(
              (val) => {
                if (!val || val === "null" || val === "undefined") return true;
                if (field.options && field.options.length > 0) {
                  return field.options.some(
                    (opt) => String(opt.value) === val && !opt.disabled,
                  );
                }
                return true;
              },
              { message: `Please select a valid ${field.label || field.name}` },
            );
          }
          break;
        }

        case "multi-select":
          let multiSelectSchema: any = z.array(
            z.union([z.string(), z.number(), z.any()]),
          );
          if (field.required) {
            multiSelectSchema = multiSelectSchema.min(
              1,
              `${field.label || field.name} is required`,
            );
          }
          fieldSchema = field.required
            ? multiSelectSchema
            : multiSelectSchema.optional();
          break;

        case "checkbox":
          fieldSchema = z.boolean();
          break;

        case "radio":
          if (field.options && field.options.length > 0) {
            const validValues = field.options.map((opt) => String(opt.value));
            if (validValues.length === 1) {
              fieldSchema = z.literal(validValues[0]);
            } else {
              fieldSchema = z.enum(validValues as [string, ...string[]]);
            }
            fieldSchema = fieldSchema.or(
              z
                .union([z.string(), z.number(), z.boolean()])
                .refine(
                  (val) =>
                    field.options?.some(
                      (opt) =>
                        opt.value === val || String(opt.value) === String(val),
                    ),
                  "Please select a valid option",
                ),
            );
          } else {
            fieldSchema = z.string();
          }
          break;

        case "switch":
          fieldSchema = z.coerce.boolean().default(false);
          break;

        case "file":
          fieldSchema = z.any().refine(
            (value) => {
              if (!field.required && (!value || value === null)) return true;
              if (field.required && (!value || value === null)) return false;
              if (value instanceof File) return true;
              if (value instanceof FileList && value.length > 0) return true;
              if (Array.isArray(value) && value.length > 0) return true;
              if (typeof value === "string" && value.length > 0) return true;
              return !field.required;
            },
            field.required ? "Please select a file" : undefined,
          );
          break;

        case "multi-file":
          if (field.multiple) {
            fieldSchema = z
              .array(z.any())
              .refine(
                (files) => {
                  if (!field.required && (!files || files.length === 0))
                    return true;
                  if (field.required && (!files || files.length === 0))
                    return false;
                  return (
                    Array.isArray(files) &&
                    files.every((file) => file instanceof File)
                  );
                },
                field.required ? "Please select at least one file" : undefined,
              )
              .refine(
                (files) => {
                  if (!files || files.length === 0) return true;
                  if (field.maxFiles && files.length > field.maxFiles)
                    return false;
                  if (field.maxSize) {
                    const maxSizeBytes = field.maxSize * 1024 * 1024;
                    return files.every((file) => file.size <= maxSizeBytes);
                  }
                  return true;
                },
                `File validation failed. Check file size (max ${field.maxSize || 10}MB) and count (max ${field.maxFiles || 10} files)`,
              );
          } else {
            fieldSchema = z.any().refine(
              (value) => {
                if (!field.required && (!value || value === null)) return true;
                if (field.required && (!value || value === null)) return false;
                return value instanceof File;
              },
              field.required ? "Please select a file" : undefined,
            );
          }
          break;

        case "range":
          fieldSchema = z
            .number()
            .min(field.min || 0)
            .max(field.max || 100);
          break;

        case "color":
          fieldSchema = z
            .string()
            .regex(/^#[0-9A-F]{6}$/i, "Please enter a valid color");
          break;

        case "form-array":
          try {
            let formArraySchema;
            if (field.itemFields && field.itemFields.length > 0) {
              const itemSchemaShape: Record<string, any> = {};

              let itemFieldsArray: FormField[] = [];
              if (Array.isArray(field.itemFields)) {
                itemFieldsArray = field.itemFields;
              } else if (typeof field.itemFields === "function") {
                // Call with index 0 and undefined formValues for schema generation
                itemFieldsArray = field.itemFields(0, undefined);
              }
              itemFieldsArray.forEach((itemField) => {
                switch (itemField.type) {
                  case "text":
                  case "textarea":
                    itemSchemaShape[itemField.name] = itemField.required
                      ? z
                          .string()
                          .min(
                            1,
                            `${itemField.label || itemField.name} is required`,
                          )
                      : z.string().optional();
                    break;
                  // case "number":
                  //   itemSchemaShape[itemField.name] = itemField.required
                  //     ? z.number()
                  //     : z.number().optional();
                  //   break;
                  case "number":
                    if (itemField.required) {
                      itemSchemaShape[itemField.name] = z.preprocess(
                        (val) => {
                          if (val === "" || val === undefined || val === null)
                            return undefined;
                          const num = Number(val);
                          return isNaN(num) ? val : num;
                        },
                        z.number({
                          required_error: "Please enter a valid number",
                          invalid_type_error: "Please enter a valid number",
                        }),
                      );
                      if (itemField.min !== undefined)
                        itemSchemaShape[itemField.name] = itemSchemaShape[
                          itemField.name
                        ].pipe(z.number().min(itemField.min));
                      if (itemField.max !== undefined)
                        itemSchemaShape[itemField.name] = itemSchemaShape[
                          itemField.name
                        ].pipe(z.number().max(itemField.max));
                    } else {
                      itemSchemaShape[itemField.name] = z.preprocess(
                        (val) => {
                          if (val === "" || val === undefined || val === null)
                            return undefined;
                          const num = Number(val);
                          return isNaN(num) ? val : num;
                        },
                        z
                          .number({
                            invalid_type_error: "Please enter a valid number",
                          })
                          .optional(),
                      );
                    }
                    break;
                  case "decimal": {
                    // Build base decimal schema
                    let itemDecimalSchema: z.ZodTypeAny = z
                      .union([
                        z.number(),
                        z
                          .string()
                          .regex(
                            /^-?\d+(\.\d{0,7})?$/,
                            "Please enter a valid decimal number",
                          ),
                      ])
                      .transform((val) =>
                        typeof val === "string" ? parseFloat(val) : val,
                      );

                    // Apply allowNegativeValue constraint (min 0)
                    const effectiveMin =
                      itemField.allowNegativeValue === false
                        ? 0
                        : itemField.min;
                    const effectiveMinMsg =
                      itemField.allowNegativeValue === false
                        ? itemField.validation?.min?.message ||
                          "Value must be 0 or greater"
                        : itemField.validation?.min?.message;

                    if (effectiveMin !== undefined)
                      itemDecimalSchema = itemDecimalSchema.pipe(
                        z.number().min(effectiveMin, effectiveMinMsg),
                      );
                    if (itemField.max !== undefined)
                      itemDecimalSchema = itemDecimalSchema.pipe(
                        z
                          .number()
                          .max(
                            itemField.max,
                            itemField.validation?.max?.message,
                          ),
                      );

                    if (itemField.required) {
                      itemSchemaShape[itemField.name] = itemDecimalSchema;
                    } else {
                      // For optional: allow empty string/null/undefined but validate if a value is provided
                      itemSchemaShape[itemField.name] = z
                        .union([z.string(), z.number()])
                        .nullish()
                        .transform((val) =>
                          val === "" || val === null || val === undefined
                            ? undefined
                            : val,
                        )
                        .pipe(itemDecimalSchema.optional());
                    }
                    break;
                  }

                  case "email":
                    itemSchemaShape[itemField.name] = itemField.required
                      ? z
                          .string()
                          .min(
                            1,
                            `${itemField.label || itemField.name} is required`,
                          )
                          .email("Please enter a valid email address")
                      : z
                          .string()
                          .email("Please enter a valid email address")
                          .optional()
                          .or(z.literal(""));
                    break;
                  case "multi-select": {
                    let msSchema = z.array(
                      z.union([z.string(), z.number(), z.any()]),
                    );
                    if (itemField.required) {
                      msSchema = msSchema.min(
                        1,
                        `${itemField.label || itemField.name} is required`,
                      );
                    }
                    itemSchemaShape[itemField.name] = itemField.required
                      ? msSchema
                      : msSchema.optional();
                    break;
                  }
                  case "select":
                    itemSchemaShape[itemField.name] = z
                      .union([z.string(), z.number(), z.array(z.any())])
                      .transform((val) => {
                        if (Array.isArray(val)) {
                          return val.length > 0 ? String(val[0]) : "";
                        }
                        return String(val ?? "");
                      });
                    if (itemField.required) {
                      itemSchemaShape[itemField.name] = itemSchemaShape[
                        itemField.name
                      ].refine(
                        (val) =>
                          val !== "" && val !== "null" && val !== "undefined",
                        {
                          message: `${
                            itemField.label || itemField.name
                          } is required`,
                        },
                      );
                    } else {
                      itemSchemaShape[itemField.name] =
                        itemSchemaShape[itemField.name].optional();
                    }
                    break;
                  case "checkbox":
                    itemSchemaShape[itemField.name] = z.boolean();
                    break;
                  default:
                    itemSchemaShape[itemField.name] = z.any().optional();
                }
              });

              const itemSchema = z.object(itemSchemaShape);
              formArraySchema = z.array(itemSchema);
            } else {
              formArraySchema = z.array(z.any());
            }

            if (field.required) {
              if (field.minItems !== undefined && field.minItems > 0) {
                formArraySchema = formArraySchema.min(
                  field.minItems,
                  `At least ${field.minItems} ${
                    field.label || "items"
                  } required`,
                );
              } else {
                formArraySchema = formArraySchema.min(
                  1,
                  `${field.label || field.name} is required`,
                );
              }
            }

            if (field.maxItems !== undefined) {
              formArraySchema = formArraySchema.max(
                field.maxItems,
                `Maximum ${field.maxItems} ${field.label || "items"} allowed`,
              );
            }

            fieldSchema = field.required
              ? formArraySchema
              : formArraySchema.optional();
          } catch (error) {
            console.warn(
              `Error creating form-array schema for ${field.name}:`,
              error,
            );
            let fallbackSchema = z.array(z.any());
            if (field.required) {
              fallbackSchema = fallbackSchema.min(
                1,
                `${field.label || field.name} is required`,
              );
            }
            fieldSchema = field.required
              ? fallbackSchema
              : fallbackSchema.optional();
          }
          break;

        case "tags": {
          const tagSchema = z
            .string()
            .trim()
            .min(1, "Tag cannot be empty")
            .max(25, "Tag cannot exceed 25 characters");

          let baseArraySchema = z.array(tagSchema);

          if (field.required) {
            baseArraySchema = baseArraySchema.min(
              1,
              `${field.label || EnhancedFormService.formatFieldName(field.name)} is required`,
            );
          }

          fieldSchema = field.required
            ? baseArraySchema
            : baseArraySchema.optional();
          break;
        }
        case "text-with-placeholder":
          if (!field.required) {
            let baseSchema = z.string();
            if (field.pattern)
              baseSchema = baseSchema.regex(
                new RegExp(field.pattern),
                "Invalid format",
              );
            if (field.minLength)
              baseSchema = baseSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              baseSchema = baseSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = z.union([z.literal(""), baseSchema]).optional();
          } else {
            let stringSchema = z.string();
            if (field.pattern)
              stringSchema = stringSchema.regex(
                new RegExp(field.pattern),
                "Invalid format",
              );
            if (field.minLength)
              stringSchema = stringSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              stringSchema = stringSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = stringSchema;
          }
          break;

        case "textarea-with-placeholder":
          if (!field.required) {
            let baseSchema = z.string();
            if (field.minLength)
              baseSchema = baseSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              baseSchema = baseSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = z.union([z.literal(""), baseSchema]).optional();
          } else {
            let stringSchema = z.string();
            if (field.minLength)
              stringSchema = stringSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              stringSchema = stringSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = stringSchema;
          }
          break;
        case "textarea":
        case "text":
        default:
          if (!field.required) {
            let baseSchema = z.string();
            if (field.pattern)
              baseSchema = baseSchema.regex(
                new RegExp(field.pattern),
                "Invalid format",
              );
            if (field.minLength)
              baseSchema = baseSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              baseSchema = baseSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = z.union([z.literal(""), baseSchema]).optional();
          } else {
            let stringSchema = z.string();
            if (field.pattern)
              stringSchema = stringSchema.regex(
                new RegExp(field.pattern),
                "Invalid format",
              );
            if (field.minLength)
              stringSchema = stringSchema.min(
                field.minLength,
                `Minimum ${field.minLength} characters required`,
              );
            if (field.maxLength)
              stringSchema = stringSchema.max(
                field.maxLength,
                `Maximum ${field.maxLength} characters allowed`,
              );
            fieldSchema = stringSchema;
          }
          break;
      }

      if (
        field.required &&
        field.type !== "multi-select" &&
        field.type !== "form-array" &&
        field.type !== "select"
      ) {
        if (field.type === "checkbox") {
          fieldSchema = fieldSchema?.refine((val) => val === true, {
            message: `${field.label || field.name} is required`,
          });
        } else {
          fieldSchema = fieldSchema?.refine(
            (val) => {
              if (val === null || val === undefined || val === "") return false;
              if (Array.isArray(val) && val.length === 0) return false;
              return true;
            },
            {
              message: `${field.label || field.name} is required`,
            },
          );
        }
      } else if (
        !field.required &&
        field.type !== "multi-select" &&
        field.type !== "form-array"
      ) {
        fieldSchema = fieldSchema.optional();
      }

      if (field.customValidation) {
        fieldSchema = fieldSchema?.refine(
          (val) => {
            try {
              const result = field.customValidation!(val);
              return typeof result === "string" ? false : result;
            } catch (error) {
              console.warn(`Custom validation error for ${field.name}:`, error);
              return true;
            }
          },
          {
            message: `Invalid ${field.label || field.name}`,
          },
        );
      }

      schemaShape[field.name] = fieldSchema;
    });

    // const schema = z.object(schemaShape);
    // this.schemaCache.set(cacheKey, schema);

    // return schema;

    const schema = z.object(schemaShape).refine(
      (data) => {
        if (!data.startDate || !data.endDate) return true;

        const start = this.parseDate(data.startDate);
        const end = this.parseDate(data.endDate);

        if (!start || !end) return true;

        return end >= start;
      },
      {
        message: "End Date must be greater than Start Date",
        path: ["endDate"], // error shows on endDate field
      },
    );

    if (shouldCache) {
      this.schemaCache.set(cacheKey, schema);
    }
    return schema;
  }

  private static getDefaultValues(fields: FormField[]): Record<string, any> {
    const defaultValues: Record<string, any> = {};

    fields.forEach((field) => {
      if (field.defaultValue !== undefined) {
        defaultValues[field.name] = field.defaultValue;
      } else {
        switch (field.type) {
          case "slider":
            defaultValues[field.name] =
              field.defaultValue !== undefined
                ? field.defaultValue
                : field.min !== undefined
                  ? field.min
                  : 1;
            break;
          case "multi-file":
            defaultValues[field.name] = field.multiple ? [] : null;
            break;
          case "checkbox":
            defaultValues[field.name] = false;
            break;
          case "multi-select":
          case "form-array":
          case "tags":
            defaultValues[field.name] = [];
            break;
          case "switch":
            defaultValues[field.name] = false;
            break;
          case "number":
          case "decimal":
          case "negative-decimal":
          case "range":
            defaultValues[field.name] = field.min || 0;
            break;
          case "color":
            defaultValues[field.name] = "#000000";
            break;
          case "select":
            defaultValues[field.name] = "";
            break;
          case "text-with-placeholder":
            defaultValues[field.name] = "";
            break;

          case "textarea-with-placeholder":
            defaultValues[field.name] = "";
            break;
          default:
            defaultValues[field.name] = "";
        }
      }
    });

    return defaultValues;
  }

  static createPayload(
    formData: any,
    fields: FormField[],
  ): Record<string, any> {
    const payload: Record<string, any> = {};

    fields.forEach((field) => {
      const value = formData[field.name];
      if (value === undefined || value === null) return;

      switch (field.type) {
        case "datepicker":
          if (value instanceof Date) {
            payload[field.name] = format(value, "yyyy-MM-dd");
          } else if (typeof value === "string") {
            const parsed = this.parseDate(value);
            payload[field.name] = parsed ? format(parsed, "yyyy-MM-dd") : value;
          }
          break;
        case "time":
          if (value instanceof Date) {
            payload[field.name] = format(value, "HH:mm:ss");
          } else {
            payload[field.name] = value + ":00";
          }
          break;
        case "number":
        case "decimal":
        case "negative-decimal":
        case "range":
          payload[field.name] =
            typeof value === "string" ? parseFloat(value) : value;
          break;
        case "checkbox":
        case "switch":
          payload[field.name] = Boolean(value);
          break;
        case "multi-select":
        case "form-array":
        case "tags":
          payload[field.name] = Array.isArray(value) ? value : [];
          break;
        case "file":
          if (value && value.length > 0) {
            payload[field.name] = field.multiple ? Array.from(value) : value[0];
          }
          break;
        case "multi-file":
          if (value && Array.isArray(value) && value.length > 0) {
            payload[field.name] = field.multiple ? value : value[0];
          } else if (value instanceof File) {
            payload[field.name] = value;
          }
          break;
        default:
          payload[field.name] = value;
      }
    });

    return payload;
  }

  static populateForm(
    form: UseFormReturn<any>,
    data: any,
    fields: FormField[],
  ): void {
    const formattedData: Record<string, any> = {};

    fields.forEach((field) => {
      const value = data[field.name];
      if (value === undefined || value === null) {
        if (
          field.type === "form-array" ||
          field.type === "multi-select" ||
          field.type === "tags"
        ) {
          formattedData[field.name] = [];
        }
        return;
      }

      switch (field.type) {
        case "datepicker":
          if (typeof value === "string") {
            const parsed = this.parseDate(value);
            formattedData[field.name] = parsed
              ? format(parsed, "yyyy-MM-dd")
              : value;
          } else {
            formattedData[field.name] = value;
          }
          break;
        case "time":
          if (typeof value === "string") {
            const [hours, minutes] = value.split(":");
            // Ensure proper formatting (HH:mm:ss)
            const formattedTime = `${hours.padStart(2, "0")}:${minutes.padStart(
              2,
              "0",
            )}:00`;
            formattedData[field.name] = formattedTime;
          } else if (value instanceof Date) {
            // In case the form gives a Date object, convert it to string
            formattedData[field.name] = format(value, "HH:mm:ss");
          } else {
            formattedData[field.name] = "";
          }
          break;
          break;
        case "multi-select":
        case "form-array":
        case "tags":
          formattedData[field.name] = Array.isArray(value) ? value : [];
          break;
        case "checkbox":
        case "switch":
          formattedData[field.name] = Boolean(value);
          break;
        default:
          formattedData[field.name] = value;
      }
    });

    form.reset(formattedData);
  }

  static getVisibleFields(
    fields: FormField[],
    formValues: any,
    basePath?: string,
    maxDepth = 10,
    currentDepth = 0,
  ): FormField[] {
    if (currentDepth >= maxDepth) {
      console.warn(
        `Maximum recursion depth reached for getVisibleFields at ${basePath}`,
      );
      return fields;
    }

    return fields.filter((field) => {
      if (field.showWhen) {
        try {
          const fieldPath = basePath ? `${basePath}.${field.name}` : field.name;
          const shouldShow = field.showWhen(formValues, fieldPath);
          return Boolean(shouldShow);
        } catch (error) {
          console.warn(
            `Error evaluating showWhen for field ${field.name}:`,
            error,
          );
          return true;
        }
      }
      return true;
    });
  }

  static getGroupedFields(
    fields: FormField[],
    data: any,
  ): Record<string, FormField[]> {
    const grouped: Record<string, FormField[]> = { default: [] };

    fields.forEach((field) => {
      const group = field.group || "default";
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(field);
    });

    Object.keys(grouped).forEach((groupKey) => {
      grouped[groupKey].sort(
        (a, b) => (a.groupOrder || 0) - (b.groupOrder || 0),
      );
    });

    return grouped;
  }

  static getFieldClasses(
    field: FormField,
    globalClasses: FormClasses,
    hasError: boolean,
    isDisabled: boolean,
  ): FormFieldClasses {
    const baseClasses: FormFieldClasses = {
      container: globalClasses.fieldContainer,
      label: globalClasses.label,
      input: globalClasses.input,
      div: globalClasses.div,
      slider: globalClasses.slider,
      textarea: globalClasses.textarea,
      MultiSelectDropdown: globalClasses.select,
      select: globalClasses.select,
      checkbox: globalClasses.checkbox,
      radio: globalClasses.radio,
      file: globalClasses.file,
      error: globalClasses.error,
      helperText: globalClasses.helperText,
      required: globalClasses.required,
      verifyButton: globalClasses.verifyButton,
      helperIcon: globalClasses.helperIcon,
      tooltip: globalClasses.tooltip,
    };

    if (field.classes) {
      Object.assign(baseClasses, field.classes);
    }

    if (hasError) {
      baseClasses.input = `${baseClasses.input} ${globalClasses.invalid}`;
    }

    if (isDisabled) {
      baseClasses.input = `${baseClasses.input} ${globalClasses.disabled}`;
    }

    if (field.size) {
      baseClasses.input = `${baseClasses.input} form-${field.type}-${field.size}`;
    }

    if (field.variant) {
      baseClasses.input = `${baseClasses.input} form-${field.type}-${field.variant}`;
    }

    return baseClasses;
  }

  static getFieldError(
    form: UseFormReturn<any>,
    fieldName: string,
  ): string | undefined {
    // Handle nested paths like "common_charges.0.charge_rate"
    const parts = fieldName.split(".");
    let error: any = form.formState.errors;
    for (const part of parts) {
      if (error == null || typeof error !== "object") return undefined;
      error = error[part];
    }
    if (!error) return undefined;
    return typeof error.message === "string" ? error.message : undefined;
  }

  static hasErrors(form: UseFormReturn<any>): boolean {
    return Object.keys(form.formState.errors).length > 0;
  }

  private static parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;

    const formats = Object.values(this.DATE_FORMATS);
    for (const formatStr of formats) {
      try {
        const parsed = parse(dateStr, formatStr, new Date());
        if (isValid(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Continue to next format
      }
    }

    try {
      const nativeDate = new Date(dateStr);
      return isValid(nativeDate) ? nativeDate : null;
    } catch (error) {
      return null;
    }
  }

  static resetForm(form: UseFormReturn<any>, formFields: FormField[]): void {
    formFields.forEach((field) => {
      try {
        form.resetField(field.name);
      } catch (error) {
        console.warn(`Error resetting field ${field.name}:`, error);
      }
    });
  }

  static clearErrors(form: UseFormReturn<any>): void {
    form.clearErrors();
  }

  static async validateFields(
    form: UseFormReturn<any>,
    fieldNames: string[],
  ): Promise<boolean> {
    try {
      const results = await Promise.all(
        fieldNames.map(async (fieldName) => {
          try {
            return await form.trigger(fieldName);
          } catch (error) {
            console.warn(`Error validating field ${fieldName}:`, error);
            return false;
          }
        }),
      );
      return results.every((result) => result);
    } catch (error) {
      console.warn("Error in validateFields:", error);
      return false;
    }
  }

  static async validateForm(form: UseFormReturn<any>): Promise<boolean> {
    try {
      const result = await form.trigger();
      return result;
    } catch (error) {
      console.warn("Error validating form:", error);
      return false;
    }
  }

  static getFieldDependencies(fields: FormField[]): Record<string, string[]> {
    const dependencies: Record<string, string[]> = {};
    fields.forEach((field) => {
      if (field.dependsOn) {
        if (!dependencies[field.dependsOn]) {
          dependencies[field.dependsOn] = [];
        }
        dependencies[field.dependsOn].push(field.name);
      }
    });
    return dependencies;
  }

  static validateDependencies(
    fields: FormField[],
    formValues: any,
  ): Record<string, string> {
    const errors: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.dependsOn && field.required) {
        const dependentValue = formValues[field.dependsOn];
        const currentValue = formValues[field.name];
        if (dependentValue && !currentValue) {
          errors[field.name] = `${field.label || field.name} is required when ${
            field.dependsOn
          } is provided`;
        }
      }
    });
    return errors;
  }

  static getFormCompletionPercentage(
    fields: FormField[],
    formValues: any,
  ): number {
    try {
      const visibleFields = this.getVisibleFields(fields, formValues);
      const requiredFields = visibleFields.filter(
        (f) => f.required && f.type !== "hidden",
      );
      if (requiredFields.length === 0) return 100;

      const completedFields = requiredFields.filter((field) => {
        const value = formValues[field.name];
        if (field.type === "checkbox") {
          return value === true;
        }
        if (field.type === "multi-select") {
          return Array.isArray(value) && value.length > 0;
        }
        return value !== undefined && value !== null && value !== "";
      });

      return Math.round((completedFields.length / requiredFields.length) * 100);
    } catch (error) {
      console.warn("Error calculating form completion percentage:", error);
      return 0;
    }
  }

  static exportFormData(
    formData: any,
    fields: FormField[],
    format: "json" | "csv" = "json",
  ): string {
    try {
      const payload = this.createPayload(formData, fields);
      if (format === "json") {
        return JSON.stringify(payload, null, 2);
      }
      if (format === "csv") {
        const headers = fields.map((f) => f.label || f.name).join(",");
        const values = fields
          .map((f) => {
            const value = payload[f.name];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value || "";
          })
          .join(",");
        return `${headers}\n${values}`;
      }
      return "";
    } catch (error) {
      console.warn("Error exporting form data:", error);
      return "";
    }
  }

  static importFormData(
    form: UseFormReturn<any>,
    importData: string,
    fields: FormField[],
    format: "json" | "csv" = "json",
  ): boolean {
    try {
      let parsedData: any = {};
      if (format === "json") {
        parsedData = JSON.parse(importData);
      } else if (format === "csv") {
        const lines = importData.split("\n");
        if (lines.length < 2) return false;
        const headers = lines[0].split(",");
        const values = lines[1].split(",");
        headers.forEach((header, index) => {
          const field = fields.find(
            (f) => (f.label || f.name) === header.trim(),
          );
          if (field && values[index]) {
            let value = values[index].trim();
            if (value.startsWith('"') && value.endsWith('"')) {
              value = value.slice(1, -1);
            }
            parsedData[field.name] = value;
          }
        });
      }
      this.populateForm(form, parsedData, fields);
      return true;
    } catch (error) {
      console.error("Failed to import form data:", error);
      return false;
    }
  }

  static generateFieldSummary(fields: FormField[]): {
    total: number;
    required: number;
    optional: number;
    byType: Record<string, number>;
    withVerification: number;
    withTooltips: number;
  } {
    const summary = {
      total: fields.length,
      required: 0,
      optional: 0,
      byType: {} as Record<string, number>,
      withVerification: 0,
      withTooltips: 0,
    };

    fields.forEach((field) => {
      if (field.required) {
        summary.required++;
      } else {
        summary.optional++;
      }
      summary.byType[field.type] = (summary.byType[field.type] || 0) + 1;
      if (field.showVerifyButton) {
        summary.withVerification++;
      }
      if (field.showHelperTooltip) {
        summary.withTooltips++;
      }
    });

    return summary;
  }

  static isProbablyDate(value: any): boolean {
    if (value instanceof Date) return isValid(value);
    if (typeof value === "string") {
      try {
        const isoDate = parseISO(value);
        if (isValid(isoDate)) return true;
      } catch (error) {
        // Continue
      }
      const formatsToTry = [
        "yyyy-MM-dd",
        "dd-MM-yyyy",
        "MM/dd/yyyy",
        "dd/MM/yyyy",
        "MMMM d, yyyy",
        "MMM d, yyyy",
        "yyyy/MM/dd",
      ];
      for (const format of formatsToTry) {
        try {
          const parsed = parse(value, format, new Date());
          if (isValid(parsed)) return true;
        } catch (error) {
          // Continue to next format
        }
      }
      return false;
    }
    return false;
  }

  static safeParseDate(value: any): Date | null {
    if (value instanceof Date && isValid(value)) return value;
    if (typeof value === "string") {
      try {
        const isoDate = parseISO(value);
        if (isValid(isoDate)) return isoDate;
      } catch (error) {
        // Continue
      }
      const formatsToTry = [
        "yyyy-MM-dd",
        "dd-MM-yyyy",
        "MM/dd/yyyy",
        "dd/MM/yyyy",
        "MMMM d, yyyy",
        "MMM d, yyyy",
        "yyyy/MM/dd",
      ];
      for (const format of formatsToTry) {
        try {
          const parsed = parse(value, format, new Date());
          if (isValid(parsed)) return parsed;
        } catch (error) {
          // Continue to next format
        }
      }
      return null;
    }
    return null;
  }

  static getUpdatedFields(
    originalData: Record<string, any>,
    editedData: Record<string, any>,
  ): Record<string, any> {
    const updatedFields: Record<string, any> = {};
    try {
      Object.keys(editedData).forEach((key) => {
        const originalValue = originalData[key];
        const editedValue = editedData[key];

        if (Array.isArray(originalValue) && Array.isArray(editedValue)) {
          if (
            originalValue.length !== editedValue.length ||
            !originalValue.every((val, index) => val === editedValue[index])
          ) {
            updatedFields[key] = editedValue;
          }
        } else if (
          this.isProbablyDate(originalValue) &&
          this.isProbablyDate(editedValue)
        ) {
          const date1 = this.safeParseDate(originalValue);
          const date2 = this.safeParseDate(editedValue);
          if (date1 && date2 && !isSameDay(date1, date2)) {
            updatedFields[key] = editedValue;
          }
        } else if (originalValue !== editedValue) {
          if (
            !(
              (originalValue === null ||
                originalValue === undefined ||
                originalValue === "") &&
              (editedValue === null ||
                editedValue === undefined ||
                editedValue === "")
            )
          ) {
            updatedFields[key] = editedValue;
          }
        }
      });
    } catch (error) {
      console.warn("Error getting updated fields:", error);
    }
    return updatedFields;
  }

  static clearSchemaCache(): void {
    this.schemaCache.clear();
  }
}

export { EnhancedFormService as FormService };
